import React, { useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { parseError } from '../../utils/errorHandlers';
import { BRAND_LOGO, LEGAL_LINKS } from '../../constants';
import './TermsAcceptance.css';

/**
 * Full-page terms gate (task 05). ProtectedRoute renders this INSTEAD of the
 * app while termsStatus is not 'clear', so nothing mounts behind it and there
 * is no dismiss path. Sidebar does not render either, so Sign out lives here.
 *
 * 'required' asks for consent. 'unverified' means the status check failed:
 * it offers a retry rather than a consent request it may be unable to save.
 */
const TermsAcceptance: React.FC = () => {
  const { termsStatus, acceptTerms, recheckTerms, logout } = useAuth();
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // A ref as well as state: two clicks in the same tick both see busy false.
  const inFlight = useRef(false);

  // busy is cleared however the action ends. A successful accept unmounts
  // this component, but recheckTerms never throws: a re-check that fails
  // again leaves the status 'unverified' and this component mounted, so
  // clearing busy only on a throw would lock Try again until a reload.
  const run = async (action: () => Promise<void>) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      await action();
    } catch (err) {
      setError(parseError(err).userMessage);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const unverified = termsStatus === 'unverified';

  return (
    <div className="terms-acceptance">
      {/* The brand header band, as PageLayout's page header. */}
      <div className="terms-acceptance-band" />
      <div className="terms-acceptance-content">
        <div className="card terms-acceptance-card">
          <div className="card-body">
            <img
              src={BRAND_LOGO.src}
              alt={BRAND_LOGO.alt}
              className="terms-acceptance-logo"
              width="175"
              height="60"
            />
            {unverified ? (
              <>
                <h1 className="terms-acceptance-title">{"We couldn't check your account"}</h1>
                <p className="terms-acceptance-text">Please try again in a moment.</p>
              </>
            ) : (
              <>
                <h1 className="terms-acceptance-title">Before you continue</h1>
                <p className="terms-acceptance-text">
                  {"Please review and accept PropWise's Terms of Service and Privacy Policy."}
                </p>
                <div className="form-group">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="termsAccept"
                      checked={checked}
                      onChange={(e) => setChecked(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="termsAccept">
                      I agree to the{' '}
                      <a href={LEGAL_LINKS[0].href} target="_blank" rel="noopener noreferrer">{LEGAL_LINKS[0].label}</a>
                      {' '}and{' '}
                      <a href={LEGAL_LINKS[1].href} target="_blank" rel="noopener noreferrer">{LEGAL_LINKS[1].label}</a>
                    </label>
                  </div>
                </div>
              </>
            )}

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <div className="terms-acceptance-actions">
              {unverified ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => run(recheckTerms)}
                >
                  Try again
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!checked || busy}
                  onClick={() => run(acceptTerms)}
                >
                  Continue
                </button>
              )}
              <button type="button" className="btn btn-outline-secondary" onClick={() => logout()}>
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAcceptance;
